<template>
  <div class="main-dashboard-container">
    <!-- Professional Header Section -->
    <div class="dashboard-header">
      <div class="header-content">
        <div class="header-title-section">
          <h1 class="dashboard-title">Property Dashboard</h1>
          <p class="dashboard-subtitle">Manage and monitor your properties</p>
        </div>
        <div class="header-actions" v-if="canShowAddProperty">
          <button @click="openAddModal" class="header-add-btn">
            <span class="btn-icon">+</span>
            <span>Add New Property</span>
          </button>
        </div>
        <div class="header-stats" v-if="!isLoading && objects.length > 0">
          <div class="stat-card">
            <div class="stat-number">{{ objects.length }}</div>
            <div class="stat-label">Total Properties</div>
          </div>
          <div class="stat-card">
            <div class="stat-number">{{ getActiveCount() }}</div>
            <div class="stat-label">Active</div>
          </div>
          <div class="stat-card">
            <div class="stat-number">{{ getPendingCount() }}</div>
            <div class="stat-label">Pending</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="skeleton-container">
      <div class="skeleton-header">
        <div class="skeleton-title"></div>
        <div class="skeleton-stats">
          <div v-for="n in 3" :key="n" class="skeleton-stat"></div>
        </div>
      </div>
      <!-- Skeleton for object cards -->
      <div class="skeleton-cards-grid">
        <div v-for="n in 6" :key="n" class="skeleton-card">
          <div class="skeleton-icon"></div>
          <div class="skeleton-name"></div>
          <div class="skeleton-status"></div>
        </div>
        <!-- Skeleton for Add New button (only for MASTER users) -->
        <div v-if="canShowAddProperty" class="skeleton-add-new">
          <div class="skeleton-plus-icon"></div>
          <div class="skeleton-add-new-text"></div>
        </div>
      </div>
    </div>

    <!-- Content Section -->
    <div v-else class="dashboard-content">
      <!-- Quick Actions Bar -->
      <div class="quick-actions" v-if="objects.length > 0">
        <div class="filter-chips">
          <button 
            v-for="status in ['ALL', 'ACTIVE', 'PENDING', 'REJECTED']" 
            :key="status"
            class="filter-chip"
            :class="{ active: selectedFilter === status }"
            @click="setFilter(status)"
          >
            {{ status }}
            <span class="chip-count" v-if="status !== 'ALL'">{{ getStatusCount(status) }}</span>
          </button>
        </div>
      </div>

      <!-- Properties Grid -->
      <div class="cards-container" v-if="filteredObjects.length > 0">
                <!-- Add New Card (only for MASTER users) -->
                <div v-if="canShowAddProperty" class="add-new-card" @click="openAddModal">
          <div class="add-new-content">
            <div class="add-new-icon">
              <font-awesome-icon :icon="['fas', 'plus']" />
            </div>
            <h3 class="add-new-title">Add New Property</h3>
            <p class="add-new-description">Create a new property listing</p>
          </div>
        </div>
        <!-- Property Cards -->
        <div
          v-for="object in filteredObjects"
          :key="object.objectId"
          class="object-card"
          :class="{ 'object-card--billing-blocked': isPropertyBillingRemoved(object.objectId) }"
          @click="onPropertyCardClick(object)"
        >
          <div class="card-header">
            <img :src="object.objectImage || defaultImage" class="object-icon" alt="Property Image" />
            <div class="card-actions">
              <!-- Notification Bell Icon -->
              <div class="notification-bell-wrapper">
                <font-awesome-icon :icon="['fas', 'bell']" class="notification-bell-icon" />
                <span v-if="getNotificationCount(object.objectId) > 0" class="notification-badge">{{ getNotificationCount(object.objectId) }}</span>
              </div>
              <button v-if="isMasterUser" class="action-btn" @click.stop="editObject(object)" title="Edit Property">
                <font-awesome-icon :icon="['fas', 'pen']" />
              </button>
            </div>
          </div>
          <div class="card-body">
            <h3 class="object-name">{{ object.objectName }}</h3>
            <div class="object-meta">
              <span class="meta-item">
                <font-awesome-icon :icon="['fas', 'map-marker-alt']" class="meta-icon" />
                Property ID: {{ object.objectId }}
              </span>
            </div>
            <div
              v-if="subscriptionResolved && propertySubscriptionStatus(object.objectId)"
              class="subscription-billing-row"
            >
              <span class="subscription-billing-row-label">Plan billing</span>
              <span
                class="subscription-billing-tag"
                :class="subscriptionBillingTagModifier(propertySubscriptionStatus(object.objectId))"
              >{{ formatPropertySubscriptionStatus(propertySubscriptionStatus(object.objectId)) }}</span>
            </div>
          </div>
          <div class="card-footer">
            <div class="status-badge" :class="getStatusClass(object.status)">
              <span class="status-dot"></span>
              {{ object.status }}
            </div>
            <div class="card-arrow">
              <font-awesome-icon :icon="['fas', 'arrow-right']" />
            </div>
          </div>
        </div>

      </div>

      <!-- Empty State -->
      <div v-else-if="objects.length === 0" class="empty-state">
        <div class="empty-state-content">
          <img src="../assets/illustration1.png" alt="No Properties" class="empty-illustration" />
          <p class="empty-description">
            {{ showSubscribeAction
              ? 'Subscribe to a plan to start adding properties to your dashboard'
              : 'Get started by adding your first property to the dashboard' }}
          </p>
          <button v-if="showSubscribeAction" @click="openSubscriptionModal" class="empty-action-btn">
            <font-awesome-icon :icon="['fas', 'plus']" class="btn-icon" />
            Subscribe
          </button>
          <button v-else-if="canShowAddProperty" @click="openAddModal" class="empty-action-btn">
            <font-awesome-icon :icon="['fas', 'plus']" class="btn-icon" />
            Add Your First Property
          </button>
        </div>
      </div>

      <!-- No Results State -->
      <div v-else class="no-results-state">
        <div class="no-results-content">
          <div class="no-results-icon">
            <font-awesome-icon :icon="['fas', 'search']" />
          </div>
          <h3 class="no-results-title">No properties match your filter</h3>
          <p class="no-results-description">Try adjusting your filter or add a new property</p>
          <button @click="setFilter('ALL')" class="secondary-btn">Clear Filter</button>
        </div>
      </div>
    </div>

    <div v-if="statusToastMessage" :class="['custom-toast', statusToastType]">
      {{ statusToastMessage }}
    </div>

<div v-if="showAddModal" class="modal-overlay" @click="closeAddModal">
  <div class="modal-content" @click.stop>
    <AddNewobject
      :edit-data="editObjectData"
      @close="closeAddModal"
      @saved="onPropertySaved"
    />
  </div>
</div>
  </div>
</template>

<script>
import AddNewobject from './AddNewObject.vue';
import defaultImage from '../assets/propertyimg.svg';
import apiService from '@/services/api';
import {
  HMS_SUBSCRIPTION_INACTIVE_EVENT,
  isNoDataError,
  isHmsSubscriptionInactiveError,
} from '../utilities/errorHelper';
import notificationService from '@/services/notificationService';
import { clearRefreshToken } from '../utilities/refreshTokenStorage';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPen, faPlus, faMapMarkerAlt, faArrowRight, faSearch, faBell } from '@fortawesome/free-solid-svg-icons';
import { library } from '@fortawesome/fontawesome-svg-core';

library.add(faPen, faPlus, faMapMarkerAlt, faArrowRight, faSearch, faBell);

export default {
  name: 'MainDashboardView',
  components: {
    AddNewobject,
    FontAwesomeIcon,
  },
  data() {
    return {
      showAddobjectForm: false,
      objects: [],
      defaultImage: defaultImage,
      isLoading: true,
      showAddModal: false,
      editObjectData: null,
      selectedFilter: 'ALL',
      adminProfileData: {},
      notificationUpdateKey: 0, // For forcing reactivity
      totalObjectCount: 0,
      objectCapacity: null,
      /** When present (new API), Add property is shown only if this is greater than 0. */
      remainingSlots: null,
      subscriptionResolved: false,
      hasActiveSubscription: false,
      subscriptionCacheDuration: 600000, // 10 minutes
      statusToastMessage: '',
      statusToastType: 'error',
      statusToastTimeout: null,
      /** objectId -> subscriptionObjects[].status (uppercase) */
      subscriptionObjectStatusById: {},
    };
  },
  computed: {
    filteredObjects() {
      if (this.selectedFilter === 'ALL') {
        return this.objects;
      }
      return this.objects.filter(object => object.status === this.selectedFilter);
    },
    isMasterUser() {
      return this.adminProfileData?.userType === 'MASTER';
    },
    hasReachedObjectCapacity() {
      if (this.objectCapacity == null) {
        return false;
      }
      const capacity = Number(this.objectCapacity);
      const count = Math.max(
        Number(this.totalObjectCount || 0),
        Array.isArray(this.objects) ? this.objects.length : 0
      );
      if (!Number.isFinite(capacity)) {
        return false;
      }
      return count >= capacity;
    },
    canShowAddProperty() {
      if (!this.isMasterUser || !this.subscriptionResolved || !this.hasActiveSubscription) {
        return false;
      }
      if (this.hasRemainingSlotsFromApi) {
        return Number(this.remainingSlots) > 0;
      }
      const hasNumericCapacity = this.objectCapacity != null && Number.isFinite(Number(this.objectCapacity));
      const allowedByCapacity = !hasNumericCapacity || !this.hasReachedObjectCapacity;
      return allowedByCapacity;
    },
    /** `remainingSlots` sent explicitly (including 0); omit legacy payloads without this field. */
    hasRemainingSlotsFromApi() {
      return this.remainingSlots !== null && this.remainingSlots !== undefined && Number.isFinite(Number(this.remainingSlots));
    },
    showSubscribeAction() {
      return this.isMasterUser && this.subscriptionResolved && !this.hasActiveSubscription;
    }
  },
  async mounted() {
    // Load admin profile data
    this.loadAdminProfileData();
    // Load cached data and fetch fresh data
    this.loadCachedObjects();
    this.loadCachedSubscriptionDetails();
    await Promise.all([
      this.fetchObjects(),
      this.fetchSubscriptionDetails({ force: true }),
    ]);
    // Load notifications from service (will fetch from API only on page refresh)
    await notificationService.getNotifications(true); // Force refresh on page load
    this.notificationUpdateKey++; // Trigger reactivity
    
    // Subscribe to notification updates
    this.unsubscribeNotifications = notificationService.subscribe(() => {
      this.notificationUpdateKey++; // Trigger reactivity when notifications change
    });
    window.addEventListener('nexthome:object-list-refreshed', this.handleObjectListRefreshEvent);
  },
  
  beforeUnmount() {
    // Unsubscribe from notifications
    if (this.unsubscribeNotifications) {
      this.unsubscribeNotifications();
    }
    window.removeEventListener('nexthome:object-list-refreshed', this.handleObjectListRefreshEvent);
    if (this.statusToastTimeout) {
      clearTimeout(this.statusToastTimeout);
    }
  },
  methods: {
    loadCachedSubscriptionDetails() {
      try {
        const cachedRaw = localStorage.getItem('SUBSCRIPTION_DATA');
        if (!cachedRaw) return;
        const cached = JSON.parse(cachedRaw);
        this.hasActiveSubscription = this.hasSubscriptionShape(cached);
        this.objectCapacity = this.subscriptionNumericCapacityFromPayload(cached);
        this.remainingSlots = this.subscriptionRemainingSlotsFromPayload(cached);
        this.applySubscriptionObjectStatuses(cached);
        this.subscriptionResolved = true;
      } catch (error) {
        console.error('Error loading cached subscription details:', error);
      }
    },
    applySubscriptionObjectStatuses(subscription) {
      const map = {};
      if (subscription && typeof subscription === 'object') {
        const arr = subscription.subscriptionObjects ?? subscription.subscriptionobjects;
        if (Array.isArray(arr)) {
          for (const o of arr) {
            const id = o.objectid ?? o.objectId;
            if (id == null || id === '') {
              continue;
            }
            map[String(id)] = String(o.status ?? '').toUpperCase() || 'UNKNOWN';
          }
        }
      }
      this.subscriptionObjectStatusById = map;
    },
    subscriptionNumericCapacityFromPayload(subscription) {
      if (!subscription || typeof subscription !== 'object') {
        return null;
      }
      const oc = subscription.objectcapacity ?? subscription.objectCapacity;
      if (oc !== undefined && oc !== null && oc !== '') {
        const n = Number(oc);
        return Number.isFinite(n) ? n : null;
      }
      const ps = subscription.purchasedslots ?? subscription.purchasedSlots;
      if (ps !== undefined && ps !== null && ps !== '') {
        const n = Number(ps);
        return Number.isFinite(n) ? n : null;
      }
      return null;
    },
    subscriptionRemainingSlotsFromPayload(subscription) {
      if (!subscription || typeof subscription !== 'object') {
        return null;
      }
      const raw = subscription.remainingslots ?? subscription.remainingSlots;
      if (raw === undefined || raw === null || raw === '') {
        return null;
      }
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    },
    hasSubscriptionShape(subscription) {
      if (!subscription || typeof subscription !== 'object') {
        return false;
      }
      return (
        (subscription.objectcapacity !== undefined && subscription.objectcapacity !== null) ||
        (subscription.objectCapacity !== undefined && subscription.objectCapacity !== null) ||
        (subscription.purchasedslots !== undefined && subscription.purchasedslots !== null) ||
        (subscription.purchasedSlots !== undefined && subscription.purchasedSlots !== null) ||
        (subscription.remainingslots !== undefined && subscription.remainingslots !== null) ||
        (subscription.remainingSlots !== undefined && subscription.remainingSlots !== null) ||
        Array.isArray(subscription.subscriptionObjects) ||
        Array.isArray(subscription.subscriptionobjects)
      );
    },
    resetSubscriptionDetails() {
      this.hasActiveSubscription = false;
      this.objectCapacity = null;
      this.remainingSlots = null;
      this.subscriptionObjectStatusById = {};
      localStorage.removeItem('SUBSCRIPTION_DATA');
    },
    shouldFetchSubscriptionFromApi() {
      try {
        const cachedRaw = localStorage.getItem('SUBSCRIPTION_DATA');
        if (!cachedRaw) return true;
        const cached = JSON.parse(cachedRaw);
        if (!cached || typeof cached !== 'object') return true;
        const hasSubscriptionShape =
          (cached.objectcapacity !== undefined && cached.objectcapacity !== null) ||
          (cached.objectCapacity !== undefined && cached.objectCapacity !== null) ||
          (cached.purchasedslots !== undefined && cached.purchasedslots !== null) ||
          (cached.purchasedSlots !== undefined && cached.purchasedSlots !== null) ||
          Array.isArray(cached.subscriptionObjects) ||
          Array.isArray(cached.subscriptionobjects) ||
          cached.remainingslots !== undefined ||
          cached.remainingSlots !== undefined;
        if (!hasSubscriptionShape) return true;
        const updatedOn = cached.updatedon ? Date.parse(cached.updatedon) : NaN;
        if (Number.isNaN(updatedOn)) {
          return false;
        }
        return (Date.now() - updatedOn) > this.subscriptionCacheDuration;
      } catch {
        return true;
      }
    },
    isPropertyBillingRemoved(objectId) {
      return this.propertySubscriptionStatus(objectId) === 'REMOVED';
    },
    onPropertyCardClick(object) {
      if (this.isPropertyBillingRemoved(object.objectId)) {
        this.showStatusToast(
          'Re-subscribe property from Subscription management.',
          'error'
        );
        return;
      }
      this.navigateToObjectDashboard(object);
    },
    navigateToObjectDashboard(object) {
      console.log('NavigatingtoObjectDashboard:', object);
      if (object?.status === 'PENDING') {
        this.showStatusToast('Contact Hostel Administration to Activate', 'error');
        return;
      }
      if (object?.status !== 'ACTIVE') {
        return;
      }
      this.$router.push({ name: 'ObjectDashboard', params: { id: object.objectId, objectName: object.objectName } });
    },
    showStatusToast(message, type = 'error') {
      this.statusToastMessage = message;
      this.statusToastType = type;
      if (this.statusToastTimeout) {
        clearTimeout(this.statusToastTimeout);
      }
      this.statusToastTimeout = setTimeout(() => {
        this.statusToastMessage = '';
        this.statusToastType = '';
      }, 3000);
    },
    loadCachedObjects() {
      try {
        const cachedData = localStorage.getItem('ADMIN_OBJECT_LIST');
        if (cachedData) {
          const { data, timestamp } = JSON.parse(cachedData);
          // Optional: Check cache expiry (e.g., 1 hour = 3600000 ms)
          const cacheDuration = 3600000; // 1 hour in milliseconds
          if (Date.now() - timestamp < cacheDuration) {
            this.objects = Array.isArray(data) ? data : [];
            this.totalObjectCount = this.objects.length;
            this.isLoading = false; // Show cached data immediately
          }
        }
      } catch (error) {
        console.error('Error loading cached objects:', error);
      }
    },
    async fetchObjects() {
      try {
        const response = await apiService.getObjectList();
        this.objects = response.data.data || [];
        this.totalObjectCount = Number(response.data.totalcount ?? this.objects.length ?? 0);
        // Cache the new data with a timestamp
        try {
          localStorage.setItem(
            'ADMIN_OBJECT_LIST',
            JSON.stringify({
              data: response.data.data,
              timestamp: Date.now(),
            })
          );
        } catch (error) {
          console.error('Error saving to LocalStorage:', error);
        }
      } catch (error) {
        // Handle "no data" case - set empty array without showing error
        if (isNoDataError(error)) {
          this.objects = [];
          this.totalObjectCount = 0;
          this.isLoading = false;
          return;
        }
        if (isHmsSubscriptionInactiveError(error)) {
          return;
        }

        console.error('Error fetching objects:', error);
        if (error?.response?.data.errorcode === 303) {
          clearRefreshToken();
          const deviceToken = localStorage.getItem('DEVICE_REG_TOKEN');
          localStorage.clear();
          if (deviceToken) { localStorage.setItem('DEVICE_REG_TOKEN', deviceToken); }
          this.$router.push('/login');
          return;
        }
        // If API fails but cache exists, keep cached data
        if (this.objects.length === 0 && localStorage.getItem('ADMIN_OBJECT_LIST')) {
          this.loadCachedObjects();
        }
      } finally {
        this.isLoading = false;
      }
    },
    async fetchSubscriptionDetails(options = {}) {
      const force = Boolean(options.force);
      if (!force && !this.shouldFetchSubscriptionFromApi()) {
        this.subscriptionResolved = true;
        return;
      }
      try {
        const response = await apiService.getSubscription();
        const subscription = response?.data;
        this.hasActiveSubscription = this.hasSubscriptionShape(subscription);
        this.objectCapacity = this.subscriptionNumericCapacityFromPayload(subscription);
        this.remainingSlots = this.subscriptionRemainingSlotsFromPayload(subscription);
        this.applySubscriptionObjectStatuses(subscription);
        if (subscription && typeof subscription === 'object') {
          localStorage.setItem('SUBSCRIPTION_DATA', JSON.stringify(subscription));
        }
      } catch (error) {
        if (isNoDataError(error) || isHmsSubscriptionInactiveError(error)) {
          this.resetSubscriptionDetails();
          return;
        }
        console.error('Error fetching subscription details:', error);
      } finally {
        this.subscriptionResolved = true;
      }
    },
    async onPropertySaved() {
      await this.fetchObjects();
      await this.fetchSubscriptionDetails({ force: true });
      this.closeAddModal();
    },
    async handleObjectListRefreshEvent() {
      await this.fetchObjects();
      await this.fetchSubscriptionDetails({ force: true });
    },
    propertySubscriptionStatus(objectId) {
      if (objectId == null || objectId === '') {
        return null;
      }
      return this.subscriptionObjectStatusById[String(objectId)] ?? null;
    },
    subscriptionBillingTagModifier(status) {
      const key = String(status || 'unknown').toLowerCase();
      return `subscription-billing-tag--${key}`;
    },
    formatPropertySubscriptionStatus(status) {
      if (!status) {
        return '';
      }
      return String(status).replace(/_/g, ' ');
    },
    openSubscriptionModal() {
      window.dispatchEvent(new CustomEvent(HMS_SUBSCRIPTION_INACTIVE_EVENT, {
        detail: { reason: 'subscription_required' },
      }));
    },
    openAddobjectForm() {
      this.showAddobjectForm = true;
      document.body.style.overflow = 'hidden';
    },
    closeAddobjectForm() {
      this.showAddobjectForm = false;
      document.body.style.overflow = 'auto';
    },
    getStatusColor(status) {
      switch (status) {
        case 'ACTIVE':
          return 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
        case 'PENDING':
          return 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
        case 'REJECTED':
          return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
        default:
          return 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)';
      }
    },
    getNotificationCount(objectId) {
      // Access notificationUpdateKey to make this reactive
      // eslint-disable-next-line no-unused-vars
      const _ = this.notificationUpdateKey;
      return notificationService.getNotificationCount(objectId);
    },
  openAddModal() {
    this.editObjectData = null;
    this.showAddModal = true;
    document.body.style.overflow = 'hidden';
  },
  editObject(object) {
    this.editObjectData = object;
    this.showAddModal = true;
    document.body.style.overflow = 'hidden';
  },
  closeAddModal() {
    this.showAddModal = false;
    this.editObjectData = null;
    document.body.style.overflow = 'auto';
  },
  setFilter(status) {
    this.selectedFilter = status;
  },
  getActiveCount() {
    return this.objects.filter(object => object.status === 'ACTIVE').length;
  },
  getPendingCount() {
    return this.objects.filter(object => object.status === 'PENDING').length;
  },
  getStatusCount(status) {
    return this.objects.filter(object => object.status === status).length;
  },
    getStatusClass(status) {
      return {
        'status-active': status === 'ACTIVE',
        'status-pending': status === 'PENDING',
        'status-rejected': status === 'REJECTED'
      };
    },
    loadAdminProfileData() {
      try {
        const storedData = localStorage.getItem('ADMIN_PROFILE_DATA');
        this.adminProfileData = storedData ? JSON.parse(storedData) : {};
      } catch (error) {
        console.error('Failed to parse admin profile data:', error);
        this.adminProfileData = {};
      }
    },
  },
};
</script>

<style scoped>
/* Modern Professional Dashboard Styles */

.main-dashboard-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  padding: 0 25px 0;
}

/* Dashboard Header */
.dashboard-header {
  background: linear-gradient(135deg, #5b2e8e 0%, #7c4dbd 100%);
  padding: 2rem 2rem 3rem;
  margin-bottom: 2rem;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 8px 32px rgba(91, 46, 142, 0.15);
  position: sticky;
  top: 80px;
  z-index: 100;
  overflow: hidden;
}

.dashboard-header::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  border-radius: 50%;
  transform: translate(50px, -50px);
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2rem;
  position: relative;
  z-index: 1;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-add-btn {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #ffffff;
  border-radius: 12px;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  outline: none;
  white-space: nowrap;
}

.header-add-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.header-add-btn:focus {
  outline: 2px solid rgba(255, 255, 255, 0.5);
  outline-offset: 2px;
}

.header-title-section {
  flex: 1;
}

.dashboard-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.025em;
  line-height: 1.2;
}

.dashboard-subtitle {
  font-size: 1.125rem;
  color: #cbd5e1;
  margin: 0;
  font-weight: 400;
}

.header-stats {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  position: relative;
  z-index: 2;
}

.stat-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  text-align: center;
  min-width: 120px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.stat-card:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
}

.stat-number {
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.25rem;
  line-height: 1;
}

.stat-label {
  font-size: 0.875rem;
  color: #cbd5e1;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Dashboard Content */
.dashboard-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem 2rem;
}

/* Quick Actions Bar */
.quick-actions {
  background: #ffffff;
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.actions-left {
  flex: 1;
}

.filter-chips {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.filter-chip {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  outline: none;
}

.filter-chip:hover {
  background: #e2e8f0;
  border-color: #cbd5e1;
}

.filter-chip.active {
  background: #5b2e8e;
  border-color: #5b2e8e;
  color: #ffffff;
}

.chip-count {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 0.125rem 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  min-width: 20px;
  text-align: center;
}

.filter-chip.active .chip-count {
  background: rgba(255, 255, 255, 0.25);
}

.primary-action-btn {
  background: linear-gradient(135deg, #5b2e8e 0%, #7c4dbd 100%);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.3);
  outline: none;
}

.primary-action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(91, 46, 142, 0.4);
}

.btn-icon {
  font-size: 1rem;
  font-weight: 700;
}

/* Cards Container */
.cards-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

/* Property Cards */
.object-card {
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
}

.object-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #5b2e8e 0%, #7c4dbd 100%);
  transform: scaleX(0);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.object-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.object-card:hover::before {
  transform: scaleX(1);
}

.object-card--billing-blocked {
  cursor: not-allowed;
  opacity: 0.94;
}

.object-card--billing-blocked:hover {
  transform: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.object-card--billing-blocked:hover::before {
  transform: scaleX(0);
}

.object-card--billing-blocked:hover .card-arrow {
  color: #cbd5e1;
  transform: none;
}

.card-header {
  padding: 1.5rem 1.5rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.object-icon {
  width: 80px;
  height: 80px;
  border-radius: 16px;
  object-fit: cover;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  border: 3px solid #ffffff;
}

.card-actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    position: relative;
}

.notification-bell-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: rgba(91, 46, 142, 0.08);
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.notification-bell-wrapper:hover {
    background: rgba(91, 46, 142, 0.12);
}

.notification-bell-icon {
    font-size: 16px;
    color: #5b2e8e;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.notification-bell-wrapper:hover .notification-bell-icon {
    color: #7c3aed;
    transform: scale(1.05);
}

.notification-badge {
    position: absolute;
    top: -6px;
    right: -6px;
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
    border-radius: 12px;
    min-width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    padding: 0 5px;
    box-shadow: 0 2px 6px rgba(239, 68, 68, 0.35), 0 0 0 2px white;
    border: none;
    line-height: 1;
    letter-spacing: -0.2px;
}

.action-btn {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.5rem;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  color: #64748b;
}

.action-btn:hover {
  background: #5b2e8e;
  border-color: #5b2e8e;
  color: #ffffff;
  transform: translateY(-1px);
}

.card-body {
  padding: 0 1.5rem 1rem;
}

.object-name {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
  line-height: 1.3;
  text-align: left;
}

.object-meta {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.meta-item {
  font-size: 0.875rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.meta-icon {
  font-size: 0.75rem;
  color: #94a3b8;
}

.subscription-billing-row {
  margin-top: 0.65rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem 0.5rem;
}

.subscription-billing-row-label {
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
}

.subscription-billing-tag {
  display: inline-flex;
  align-items: center;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  border: 1px solid transparent;
  line-height: 1.2;
}

.subscription-billing-tag--active {
  background: rgba(34, 197, 94, 0.14);
  color: #15803d;
  border-color: rgba(34, 197, 94, 0.28);
}

.subscription-billing-tag--cancelled {
  background: rgba(245, 158, 11, 0.16);
  color: #b45309;
  border-color: rgba(245, 158, 11, 0.35);
}

.subscription-billing-tag--removed {
  background: rgba(148, 163, 184, 0.22);
  color: #475569;
  border-color: rgba(148, 163, 184, 0.4);
}

.subscription-billing-tag--unknown {
  background: rgba(99, 102, 241, 0.1);
  color: #4338ca;
  border-color: rgba(99, 102, 241, 0.22);
}

.card-footer {
  padding: 1rem 1.5rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #f1f5f9;
}

.status-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.status-active {
  background: #ecfdf5;
  color: #065f46;
  border: 1px solid #d1fae5;
}

.status-active .status-dot {
  background: #10b981;
}

.status-pending {
  background: #fffbeb;
  color: #92400e;
  border: 1px solid #fde68a;
}

.status-pending .status-dot {
  background: #f59e0b;
}

.status-rejected {
  background: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.status-rejected .status-dot {
  background: #ef4444;
}

.card-arrow {
  color: #cbd5e1;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.object-card:hover .card-arrow {
  color: #5b2e8e;
  transform: translateX(4px);
}

/* Add New Card */
.add-new-card {
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border: 2px dashed #cbd5e1;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  position: relative;
  overflow: hidden;
}

.add-new-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(91, 46, 142, 0.05), transparent);
  transition: left 0.5s;
}

.add-new-card:hover::before {
  left: 100%;
}

.add-new-card:hover {
  border-color: #5b2e8e;
  background: linear-gradient(135deg, #f8f5ff 0%, #ffffff 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(91, 46, 142, 0.15);
}

.add-new-content {
  text-align: center;
  z-index: 1;
  position: relative;
}

.add-new-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #5b2e8e 0%, #7c4dbd 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  color: #ffffff;
  font-size: 1.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.add-new-card:hover .add-new-icon {
  transform: scale(1.1);
}

.add-new-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
}

.add-new-description {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
}

/* Empty State */
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 2rem;
}

.empty-state-content {
  text-align: center;
  max-width: 400px;
}

.empty-illustration {
  max-width: 280px;
  opacity: 0.8;
  margin-bottom: 2rem;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
}

.empty-description {
  font-size: 1rem;
  color: #64748b;
  margin: 0 0 2rem 0;
  line-height: 1.5;
}

.empty-action-btn {
  background: linear-gradient(135deg, #5b2e8e 0%, #7c4dbd 100%);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  padding: 0.875rem 1.75rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.3);
}

.empty-action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(91, 46, 142, 0.4);
}

/* No Results State */
.no-results-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  padding: 2rem;
}

.no-results-content {
  text-align: center;
  max-width: 300px;
}

.no-results-icon {
  width: 80px;
  height: 80px;
  background: #f1f5f9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  color: #64748b;
  font-size: 2rem;
}

.no-results-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
}

.no-results-description {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0 0 1.5rem 0;
}

.secondary-btn {
  background: #ffffff;
  color: #5b2e8e;
  border: 1px solid #5b2e8e;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.secondary-btn:hover {
  background: #5b2e8e;
  color: #ffffff;
}

/* Modern Skeleton Loading Styles */
.skeleton-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem 2rem;
}

.skeleton-header {
  background: #ffffff;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.skeleton-title {
  width: 300px;
  height: 32px;
  background: linear-gradient(90deg, #f1f5f9, #e2e8f0, #f1f5f9);
  background-size: 200% 100%;
  border-radius: 8px;
  margin-bottom: 1rem;
  animation: shimmer 2s infinite;
}

.skeleton-stats {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.skeleton-stat {
  width: 120px;
  height: 80px;
  background: linear-gradient(90deg, #f1f5f9, #e2e8f0, #f1f5f9);
  background-size: 200% 100%;
  border-radius: 12px;
  animation: shimmer 2s infinite;
}

.skeleton-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 1.5rem;
}

.skeleton-card {
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
  position: relative;
}

.skeleton-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(91, 46, 142, 0.05), transparent);
  animation: shimmer 2s infinite;
}

.skeleton-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(90deg, #f1f5f9, #e2e8f0, #f1f5f9);
  background-size: 200% 100%;
  border-radius: 16px;
  margin: 1.5rem 1.5rem 1rem;
  animation: shimmer 2s infinite;
}

.skeleton-name {
  width: 60%;
  height: 20px;
  background: linear-gradient(90deg, #f1f5f9, #e2e8f0, #f1f5f9);
  background-size: 200% 100%;
  border-radius: 6px;
  margin: 0 1.5rem 1rem;
  animation: shimmer 2s infinite;
}

.skeleton-status {
  width: 100px;
  height: 24px;
  background: linear-gradient(90deg, #f1f5f9, #e2e8f0, #f1f5f9);
  background-size: 200% 100%;
  border-radius: 12px;
  margin: 1rem 1.5rem 1.5rem;
  animation: shimmer 2s infinite;
}

.skeleton-add-new {
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border: 2px dashed #e2e8f0;
  border-radius: 20px;
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.skeleton-add-new::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(91, 46, 142, 0.03), transparent);
  animation: shimmer 2s infinite;
}

.skeleton-plus-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(90deg, #f1f5f9, #e2e8f0, #f1f5f9);
  background-size: 200% 100%;
  border-radius: 50%;
  margin-bottom: 1rem;
  animation: shimmer 2s infinite;
}

.skeleton-add-new-text {
  width: 120px;
  height: 18px;
  background: linear-gradient(90deg, #f1f5f9, #e2e8f0, #f1f5f9);
  background-size: 200% 100%;
  border-radius: 6px;
  animation: shimmer 2s infinite;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

.modal-content {
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(0, 0, 0, 0.05);
  width: 95vw;
  max-width: 1500px;
  max-height: 95vh;
  overflow-y: auto;
  overflow-x: hidden;
  animation: slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  display: block;
  position: relative;
}

/* Modern Animations */
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Professional Responsive Design */
@media (max-width: 1200px) {
  .header-content {
    flex-direction: column;
    gap: 1.5rem;
    align-items: center;
  }
  
  .header-actions {
    justify-content: center;
  }
  
  .header-stats {
    justify-content: center;
    order: 3;
  }
  
  .header-title-section {
    order: 1;
    text-align: center;
  }
  
  .header-actions {
    order: 2;
  }
  
  .dashboard-title {
    font-size: 2rem;
  }
}

@media (max-width: 1024px) {
  .cards-container,
  .skeleton-cards-grid {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1.25rem;
  }
  
  .quick-actions {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .filter-chips {
    justify-content: center;
  }
  
  .modal-content {
    max-height: 95vh;
    width: 96vw;
    padding-bottom: 0;
  }
}

@media (max-width: 768px) {
  .dashboard-header {
    padding: 1.5rem 1rem 2rem;
    top: 60px;
  }
  
  .dashboard-content {
    padding: 0 1rem 2rem;
  }
  
  .dashboard-title {
    font-size: 1.75rem;
  }
  
  .dashboard-subtitle {
    font-size: 1rem;
  }
  
  .header-add-btn {
    padding: 0.625rem 1.25rem;
    font-size: 0.8125rem;
  }
  
  .header-stats {
    gap: 1rem;
    justify-content: center;
  }
  
  .stat-card {
    min-width: 100px;
    padding: 1rem;
  }
  
  .stat-number {
    font-size: 1.5rem;
  }
  
  .cards-container,
  .skeleton-cards-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1rem;
  }
  
  .object-card,
  .add-new-card,
  .skeleton-card,
  .skeleton-add-new {
    min-height: 260px;
  }
  
  .card-header {
    padding: 1.25rem 1.25rem 0.75rem;
  }
  
  .card-body {
    padding: 0 1.25rem 0.75rem;
  }
  
  .card-footer {
    padding: 0.75rem 1.25rem 1.25rem;
  }
  
  .object-icon,
  .skeleton-icon {
    width: 70px;
    height: 70px;
  }
  
  .object-name {
    font-size: 1.125rem;
  }
  
  .modal-content {
    width: 96vw;
    max-width: 1200px;
    border-radius: 16px;
    max-height: 98vh;
  }
}

@media (max-width: 640px) {
  .dashboard-header {
    padding: 1rem 0.75rem 1.5rem;
  }
  
  .dashboard-content {
    padding: 0 0.75rem 1.5rem;
  }
  
  .dashboard-title {
    font-size: 1.5rem;
  }
  
  .header-add-btn {
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
  }
  
  .header-stats {
    grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
    gap: 0.75rem;
  }
  
  .stat-card {
    min-width: 80px;
    padding: 0.75rem;
  }
  
  .stat-number {
    font-size: 1.25rem;
  }
  
  .stat-label {
    font-size: 0.75rem;
  }
  
  .quick-actions {
    padding: 1rem;
  }
  
  .filter-chips {
    gap: 0.375rem;
  }
  
  .filter-chip {
    padding: 0.375rem 0.75rem;
    font-size: 0.8125rem;
  }
}

@media (max-width: 480px) {
  .cards-container,
  .skeleton-cards-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .empty-illustration {
    max-width: 240px;
  }
  
  .empty-title {
    font-size: 1.25rem;
  }
  
  .modal-content {
    width: 98vw;
    max-width: none;
    max-height: 98vh;
    border-radius: 12px;
    display: block;
    position: relative;
  }
}

/* Enhanced accessibility and focus states */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Focus states for accessibility */
.filter-chip:focus,
.primary-action-btn:focus,
.action-btn:focus,
.empty-action-btn:focus,
.secondary-btn:focus {
  outline: 2px solid #5b2e8e;
  outline-offset: 2px;
}

.object-card:focus-within {
  outline: 2px solid #5b2e8e;
  outline-offset: 2px;
}

/* Print styles */
@media print {
  .dashboard-header {
    background: #ffffff !important;
    color: #000000 !important;
    box-shadow: none !important;
  }
  
  .object-card,
  .add-new-card {
    box-shadow: none !important;
    border: 1px solid #e5e7eb !important;
    break-inside: avoid;
  }
  
  .quick-actions,
  .modal-overlay {
    display: none !important;
  }
}
</style>